<script setup lang="ts">
const props = defineProps<{
  rating: number | null
  showValue?: boolean
  size?: 'sm' | 'md'
}>()

const displayRating = computed(() => {
  if (props.rating === null) return null
  return props.rating.toFixed(1)
})

const fullStars = computed(() => {
  if (props.rating === null) return 0
  return Math.floor(props.rating)
})

const hasHalfStar = computed(() => {
  if (props.rating === null) return false
  return props.rating % 1 >= 0.5
})

const emptyStars = computed(() => {
  if (props.rating === null) return 5
  return 5 - fullStars.value - (hasHalfStar.value ? 1 : 0)
})

const svgSize = computed(() => props.size === 'sm' ? 14 : 16)
const textSize = computed(() => props.size === 'sm' ? 'text-xs' : 'text-sm')
</script>

<template>
  <div class="flex items-center gap-1">
    <div v-if="rating !== null" class="flex items-center">
      <svg
        v-for="i in fullStars"
        :key="`full-${i}`"
        xmlns="http://www.w3.org/2000/svg"
        :width="svgSize"
        :height="svgSize"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-yellow-400"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      <svg
        v-if="hasHalfStar"
        xmlns="http://www.w3.org/2000/svg"
        :width="svgSize"
        :height="svgSize"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-yellow-400"
      >
        <defs>
          <clipPath id="half-clip">
            <rect x="0" y="0" width="12" height="24" />
          </clipPath>
        </defs>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="currentColor" clip-path="url(#half-clip)" />
      </svg>
      <svg
        v-for="i in emptyStars"
        :key="`empty-${i}`"
        xmlns="http://www.w3.org/2000/svg"
        :width="svgSize"
        :height="svgSize"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-dimmed"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </div>
    <span v-if="showValue && displayRating" :class="[textSize, 'font-medium text-muted']">
      {{ displayRating }}
    </span>
  </div>
</template>
